import { DeleteButton, RegisterButton } from '../../../common/CustomButton'
import MenuIcon from '@mui/icons-material/Menu'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  useUserClassificationDispatch,
  useUserClassificationSelector,
  useUserClassificationStore
} from '../extra/store'
import { formatDateAsMonth, formatDateCustom, parseDateCustom } from '../../../../common/utils/DateUtils'
import { useOpenUserClassificationModifyCategoryModal } from './UserClassificationModifyCategoryModal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual } from 'react-redux'
import { DndContext } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { CSS } from '@dnd-kit/utilities'
import {
  hasRecordChanged,
  afterStateUpdate,
  reorderVisibleRecordsPreservingHidden,
  refreshClassificationErrors
} from '../extra/helpers'
import CustomTooltip from '../../../../_components/CustomTooltip'
import { isDateRangeOverlapping } from '../extra/validation'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'

/** @typedef {import('../extra/types').UserClassficationRecordType} UserClassificationRecordType */

export const UserClassificationCategory = (props) => {
  const { onMarkClassificationAsPending, onReset } = props
  const _records = useUserClassificationSelector(
    (s) => s.userClassificationRecords?.[s.selectedUserClassificationType] ?? [],
    shallowEqual
  )
  const selectedClassificationType = useUserClassificationSelector((s) => s.selectedUserClassificationType)

  // 現在選択中の区分のエラーを取得
  const classificationErrors = useUserClassificationSelector((s) => {
    if (!selectedClassificationType) return []
    const errorsByClassification = s.errors?.byClassification || {}
    return errorsByClassification[selectedClassificationType] || []
  })

  // インポート警告メッセージを取得（区分ごと）
  const importWarnings = useUserClassificationSelector((s) => {
    if (!selectedClassificationType) return []
    const warningsByClassification = s.errors?.importWarnings || {}
    return warningsByClassification[selectedClassificationType] || []
  })

  // インポートバリデーションエラーを取得（区分ごと）
  const importValidationErrors = useUserClassificationSelector((s) => {
    if (!selectedClassificationType) return []
    const errorsByClassification = s.errors?.importValidationErrors || {}
    return errorsByClassification[selectedClassificationType] || []
  })

  const dispatch = useUserClassificationDispatch()
  const store = useUserClassificationStore()

  // モーダル表示時のデータを保存するためのref
  const modalOpenDataRef = useRef(null)
  // モーダルを開く時点の選択中区分コードを保存するためのref
  const modalClassificationTypeRef = useRef(null)

  /**
   * @function handleModalOpen モーダル表示時にデータを保存
   * @param {UserClassificationRecordType} defaultData
   */
  const handleModalOpen = useCallback((defaultData) => {
    // モーダル表示時のデータを保存（深いコピー）
    modalOpenDataRef.current = defaultData ? JSON.parse(JSON.stringify(defaultData)) : null
  }, [])

  /**
   * @function validateRecord レコードのバリデーション（インポート時と同様のチェック）
   * @param {UserClassificationRecordType} record - チェック対象のレコード
   * @param {UserClassificationRecordType[]} existingRecords - 既存のレコード配列
   * @param {boolean} isEditMode - 編集モードかどうか
   * @returns {Object} フィールドごとのエラー情報
   */
  const validateRecord = useCallback((record, existingRecords, isEditMode) => {
    const fieldErrors = {}

    // 値の必須チェック
    if (!record.value || record.value.trim() === '') {
      if (!fieldErrors.value) {
        fieldErrors.value = []
      }
      fieldErrors.value.push('必須です')
    }

    // ランクの数値チェック
    if (record.rank != null && record.rank !== '') {
      if (isNaN(Number(record.rank))) {
        if (!fieldErrors.rank) {
          fieldErrors.rank = []
        }
        fieldErrors.rank.push('数値のみ設定可能です')
      }
    }

    // 重複チェック（既存レコードとの比較）
    const normalize = (val) => {
      if (val == null) return ''
      return String(val).trim()
    }

    const normalizedValue = normalize(record.value)
    const normalizedCode = normalize(record.code)

    existingRecords.forEach((existingRecord) => {
      // 編集モードの場合は自分自身を除外
      if (isEditMode && existingRecord.key === record.key) {
        return
      }
      // 削除済みレコードは除外
      if (existingRecord.isDeleted) {
        return
      }

      const normalizedExistingValue = normalize(existingRecord.value)
      const normalizedExistingCode = normalize(existingRecord.code)

      // 値の重複チェック
      if (normalizedValue === normalizedExistingValue && normalizedValue !== '') {
        const hasOverlap = isDateRangeOverlapping(
          record.validStartDate,
          record.validEndDate,
          existingRecord.validStartDate,
          existingRecord.validEndDate
        )
        if (hasOverlap) {
          if (!fieldErrors.value) {
            fieldErrors.value = []
          }
          if (!fieldErrors.value.includes('同一の値で有効期間が重複しているものがあります')) {
            fieldErrors.value.push('同一の値で有効期間が重複しているものがあります')
          }
        }
      }

      // コードの重複チェック
      if (normalizedCode !== '' && normalizedCode === normalizedExistingCode) {
        const hasOverlap = isDateRangeOverlapping(
          record.validStartDate,
          record.validEndDate,
          existingRecord.validStartDate,
          existingRecord.validEndDate
        )
        if (hasOverlap) {
          if (!fieldErrors.code) {
            fieldErrors.code = []
          }
          if (!fieldErrors.code.includes('同一のコードで有効期間が重複しているものがあります')) {
            fieldErrors.code.push('同一のコードで有効期間が重複しているものがあります')
          }
        }
      }
    })

    return fieldErrors
  }, [])

  /**
   * @function handleRegisterClick
   * @param {UserClassificationRecordType} value
   */
  async function handleRegisterClick(value) {
    if (!value) {
      console.error('追加失敗しました')
      return
    }

    // モーダルを開いた時点の区分コードを使用（モーダル表示中の区分変更に対応）
    const classificationCode = modalClassificationTypeRef.current || selectedClassificationType

    if (!classificationCode || classificationCode === '') {
      console.error('区分が選択されていません')
      return
    }

    dispatch('ADD_CLASSIFICATION', value)
    // 未反映状態にマーク（Reduxの状態更新を待つ）
    afterStateUpdate(() => {
      // 表のすべてのレコードに対してデータチェックを実施してエラーメッセージを更新
      const state = store.getState()
      const allRecords = state.userClassificationRecords[classificationCode] || []

      // 既存のrecordValidationErrorsを取得（他の区分のエラーを保持するため）
      const currentRecordValidationErrors = state.errors?.recordValidationErrors || {}
      const newRecordValidationErrors = { ...currentRecordValidationErrors }

      // importValidationErrors用のエラー配列
      const newImportValidationErrors = []

      // 削除済みレコードを除外した配列を作成（表示順にソート）
      const activeRecords = allRecords
        .filter((record) => !record.isDeleted)
        .sort((a, b) => {
          const orderA = parseInt(a.displayOrder, 10) || 0
          const orderB = parseInt(b.displayOrder, 10) || 0
          return orderA - orderB
        })

      // すべてのレコードをチェック（削除済みレコードのエラーは削除）
      allRecords.forEach((record) => {
        if (record.isDeleted) {
          // 削除済みレコードのエラーは削除
          if (newRecordValidationErrors[record.key]) {
            delete newRecordValidationErrors[record.key]
          }
        }
      })

      // 削除済みレコードを除外したレコードをチェック
      activeRecords.forEach((record, recordIndex) => {
        // 新規追加の場合はisEditMode=false、既存レコードの場合はisEditMode=true
        const isEditMode = !record.isCreated || record.key !== value.key
        const fieldErrors = validateRecord(record, allRecords, isEditMode)

        if (Object.keys(fieldErrors).length > 0) {
          // エラーがある場合は更新
          newRecordValidationErrors[record.key] = fieldErrors

          // importValidationErrors用の形式に変換
          Object.keys(fieldErrors).forEach((field) => {
            fieldErrors[field].forEach((message) => {
              newImportValidationErrors.push({
                recordIndex,
                field,
                message
              })
            })
          })
        } else {
          // エラーが解消された場合は削除
          if (newRecordValidationErrors[record.key]) {
            delete newRecordValidationErrors[record.key]
          }
        }
      })

      // recordValidationErrorsを更新（既存のエラーを保持しつつ、該当区分のエラーのみ更新）
      dispatch('SET_STATE', {
        path: 'errors.recordValidationErrors',
        value: newRecordValidationErrors
      })

      // importValidationErrorsを更新（表の下のエラーメッセージ表示用）
      const currentImportValidationErrors = { ...(state.errors?.importValidationErrors || {}) }
      currentImportValidationErrors[classificationCode] = newImportValidationErrors
      dispatch('SET_STATE', {
        path: 'errors.importValidationErrors',
        value: currentImportValidationErrors
      })

      // 区分単位エラー（ランク整合性・サーバー由来）を追加後の状態で再判定して除去（バグ #3293）
      refreshClassificationErrors(store, dispatch, classificationCode)

      if (onMarkClassificationAsPending) {
        onMarkClassificationAsPending(classificationCode)
      }
    })
  }

  /**
   * @function handleUpdateClick
   * @param {UserClassificationRecordType} value
   */
  async function handleUpdateClick(value) {
    // モーダルを開いた時点の区分コードを使用（モーダル表示中の区分変更に対応）
    const classificationCode = modalClassificationTypeRef.current || selectedClassificationType

    if (!classificationCode || classificationCode === '') {
      console.error('区分が選択されていません')
      return
    }

    dispatch('UPDATE_CLASSIFICATION', value)

    // 常にonMarkClassificationAsPendingを呼び出し、prepareUpdateData内で初期状態との比較により変更判定を行う
    afterStateUpdate(() => {
      // 表のすべてのレコードに対してデータチェックを実施してエラーメッセージを更新
      const state = store.getState()
      const allRecords = state.userClassificationRecords[classificationCode] || []

      // 既存のrecordValidationErrorsを取得（他の区分のエラーを保持するため）
      const currentRecordValidationErrors = state.errors?.recordValidationErrors || {}
      const newRecordValidationErrors = { ...currentRecordValidationErrors }

      // importValidationErrors用のエラー配列
      const newImportValidationErrors = []

      // 削除済みレコードを除外した配列を作成（表示順にソート）
      const activeRecords = allRecords
        .filter((record) => !record.isDeleted)
        .sort((a, b) => {
          const orderA = parseInt(a.displayOrder, 10) || 0
          const orderB = parseInt(b.displayOrder, 10) || 0
          return orderA - orderB
        })

      // すべてのレコードをチェック（削除済みレコードのエラーは削除）
      allRecords.forEach((record) => {
        if (record.isDeleted) {
          // 削除済みレコードのエラーは削除
          if (newRecordValidationErrors[record.key]) {
            delete newRecordValidationErrors[record.key]
          }
        }
      })

      // 削除済みレコードを除外したレコードをチェック
      activeRecords.forEach((record, recordIndex) => {
        const fieldErrors = validateRecord(record, allRecords, true)

        if (Object.keys(fieldErrors).length > 0) {
          // エラーがある場合は更新
          newRecordValidationErrors[record.key] = fieldErrors

          // importValidationErrors用の形式に変換
          Object.keys(fieldErrors).forEach((field) => {
            fieldErrors[field].forEach((message) => {
              newImportValidationErrors.push({
                recordIndex,
                field,
                message
              })
            })
          })
        } else {
          // エラーが解消された場合は削除
          if (newRecordValidationErrors[record.key]) {
            delete newRecordValidationErrors[record.key]
          }
        }
      })

      // recordValidationErrorsを更新（既存のエラーを保持しつつ、該当区分のエラーのみ更新）
      dispatch('SET_STATE', {
        path: 'errors.recordValidationErrors',
        value: newRecordValidationErrors
      })

      // importValidationErrorsを更新（表の下のエラーメッセージ表示用）
      const currentImportValidationErrors = { ...(state.errors?.importValidationErrors || {}) }
      currentImportValidationErrors[classificationCode] = newImportValidationErrors
      dispatch('SET_STATE', {
        path: 'errors.importValidationErrors',
        value: currentImportValidationErrors
      })

      // 区分単位エラー（ランク整合性・サーバー由来）を更新後の状態で再判定して除去（バグ #3293）
      refreshClassificationErrors(store, dispatch, classificationCode)

      if (onMarkClassificationAsPending) {
        onMarkClassificationAsPending(classificationCode)
      }
    })

    // モーダル表示時のデータをクリア
    modalOpenDataRef.current = null
    modalClassificationTypeRef.current = null
  }

  const openModifyClassification = useOpenUserClassificationModifyCategoryModal({
    create: handleRegisterClick,
    update: handleUpdateClick,
    onModalOpen: handleModalOpen
  })

  /** @function openModal 登録ためのポップアップを表示 */
  const openModal = () => {
    // モーダルを開く時点の選択中区分コードを保存
    if (!selectedClassificationType || selectedClassificationType === '') {
      console.error('区分が選択されていません')
      return
    }
    modalClassificationTypeRef.current = selectedClassificationType
    openModifyClassification(undefined, _records)
  }

  /** @typedef {function(MouseEvent, UserClassificationRecordType )} openEditModal */

  /** @type {ReturnType<typeof useCallback<openEditModal>>} */
  const openEditModal = useCallback(
    function (e, defaultData) {
      e.preventDefault()
      // モーダルを開く時点の選択中区分コードを保存
      if (!selectedClassificationType || selectedClassificationType === '') {
        console.error('区分が選択されていません')
        return
      }
      modalClassificationTypeRef.current = selectedClassificationType
      return openModifyClassification(defaultData, _records)
    },
    [selectedClassificationType, openModifyClassification]
  )

  /**
   * @typedef {function(UserClassificationRecordType , boolean)} toggleDelete
   * @type {ReturnType<typeof useCallback<toggleDelete>>} 削除状態を切り替える
   */
  const toggleDelete = useCallback(
    (e, forceState) => {
      dispatch('UPDATE_CLASSIFICATION', {
        key: e.key,
        isDeleted: forceState === undefined ? !e.isDeleted : forceState
      })
      // 未反映状態にマーク（Reduxの状態更新を待つ）
      afterStateUpdate(() => {
        // 表のすべてのレコードに対してデータチェックを実施してエラーメッセージを更新
        const state = store.getState()
        const allRecords = state.userClassificationRecords[selectedClassificationType] || []

        // 既存のrecordValidationErrorsを取得（他の区分のエラーを保持するため）
        const currentRecordValidationErrors = state.errors?.recordValidationErrors || {}
        const newRecordValidationErrors = { ...currentRecordValidationErrors }

        // importValidationErrors用のエラー配列
        const newImportValidationErrors = []

        // 削除済みレコードを除外した配列を作成（表示順にソート）
        const activeRecords = allRecords
          .filter((record) => !record.isDeleted)
          .sort((a, b) => {
            const orderA = parseInt(a.displayOrder, 10) || 0
            const orderB = parseInt(b.displayOrder, 10) || 0
            return orderA - orderB
          })

        // すべてのレコードをチェック（削除済みレコードのエラーは削除）
        allRecords.forEach((record) => {
          if (record.isDeleted) {
            // 削除済みレコードのエラーは削除
            if (newRecordValidationErrors[record.key]) {
              delete newRecordValidationErrors[record.key]
            }
          }
        })

        // 削除済みレコードを除外したレコードをチェック
        activeRecords.forEach((record, recordIndex) => {
          const fieldErrors = validateRecord(record, allRecords, true)

          if (Object.keys(fieldErrors).length > 0) {
            // エラーがある場合は更新
            newRecordValidationErrors[record.key] = fieldErrors

            // importValidationErrors用の形式に変換
            Object.keys(fieldErrors).forEach((field) => {
              fieldErrors[field].forEach((message) => {
                newImportValidationErrors.push({
                  recordIndex,
                  field,
                  message
                })
              })
            })
          } else {
            // エラーが解消された場合は削除
            if (newRecordValidationErrors[record.key]) {
              delete newRecordValidationErrors[record.key]
            }
          }
        })

        // recordValidationErrorsを更新（既存のエラーを保持しつつ、該当区分のエラーのみ更新）
        dispatch('SET_STATE', {
          path: 'errors.recordValidationErrors',
          value: newRecordValidationErrors
        })

        // importValidationErrorsを更新（表の下のエラーメッセージ表示用）
        const currentImportValidationErrors = { ...(state.errors?.importValidationErrors || {}) }
        currentImportValidationErrors[selectedClassificationType] = newImportValidationErrors
        dispatch('SET_STATE', {
          path: 'errors.importValidationErrors',
          value: currentImportValidationErrors
        })

        // 区分単位エラー（ランク整合性・サーバー由来）を削除・復帰後の状態で再判定して除去（バグ #3293）
        refreshClassificationErrors(store, dispatch, selectedClassificationType)

        if (onMarkClassificationAsPending) {
          onMarkClassificationAsPending(selectedClassificationType)
        }
      })
    },
    [dispatch, onMarkClassificationAsPending, selectedClassificationType, store, validateRecord]
  )

  /**
   * @typedef {function(UserClassificationRecordType )} handleRemove
   * @type {ReturnType<typeof useCallback<handleRemove>>} 非表示
   */
  const handleRemove = useCallback(
    (e) => {
      dispatch('REMOVE_CLASSIFICATION', {
        key: e.key
      })
      // 未反映状態にマーク（Reduxの状態更新を待つ）
      afterStateUpdate(() => {
        // 表のすべてのレコードに対してデータチェックを実施してエラーメッセージを更新
        const state = store.getState()
        const allRecords = state.userClassificationRecords[selectedClassificationType] || []

        // 既存のrecordValidationErrorsを取得（他の区分のエラーを保持するため）
        const currentRecordValidationErrors = state.errors?.recordValidationErrors || {}
        const newRecordValidationErrors = { ...currentRecordValidationErrors }

        // importValidationErrors用のエラー配列
        const newImportValidationErrors = []

        // 削除済みレコードを除外した配列を作成（表示順にソート）
        const activeRecords = allRecords
          .filter((record) => !record.isDeleted)
          .sort((a, b) => {
            const orderA = parseInt(a.displayOrder, 10) || 0
            const orderB = parseInt(b.displayOrder, 10) || 0
            return orderA - orderB
          })

        // すべてのレコードをチェック（削除済みレコードのエラーは削除）
        allRecords.forEach((record) => {
          if (record.isDeleted) {
            // 削除済みレコードのエラーは削除
            if (newRecordValidationErrors[record.key]) {
              delete newRecordValidationErrors[record.key]
            }
          }
        })

        // 削除済みレコードを除外したレコードをチェック
        activeRecords.forEach((record, recordIndex) => {
          const fieldErrors = validateRecord(record, allRecords, true)

          if (Object.keys(fieldErrors).length > 0) {
            // エラーがある場合は更新
            newRecordValidationErrors[record.key] = fieldErrors

            // importValidationErrors用の形式に変換
            Object.keys(fieldErrors).forEach((field) => {
              fieldErrors[field].forEach((message) => {
                newImportValidationErrors.push({
                  recordIndex,
                  field,
                  message
                })
              })
            })
          } else {
            // エラーが解消された場合は削除
            if (newRecordValidationErrors[record.key]) {
              delete newRecordValidationErrors[record.key]
            }
          }
        })

        // recordValidationErrorsを更新（既存のエラーを保持しつつ、該当区分のエラーのみ更新）
        dispatch('SET_STATE', {
          path: 'errors.recordValidationErrors',
          value: newRecordValidationErrors
        })

        // importValidationErrorsを更新（表の下のエラーメッセージ表示用）
        const currentImportValidationErrors = { ...(state.errors?.importValidationErrors || {}) }
        currentImportValidationErrors[selectedClassificationType] = newImportValidationErrors
        dispatch('SET_STATE', {
          path: 'errors.importValidationErrors',
          value: currentImportValidationErrors
        })

        // 区分単位エラー（ランク整合性・サーバー由来）を削除・復帰後の状態で再判定して除去（バグ #3293）
        refreshClassificationErrors(store, dispatch, selectedClassificationType)

        if (onMarkClassificationAsPending) {
          onMarkClassificationAsPending(selectedClassificationType)
        }
      })
    },
    [dispatch, onMarkClassificationAsPending, selectedClassificationType, store, validateRecord]
  )

  /**
   * @typedef {function(UserClassificationRecordType )} handleRestore
   * @type {ReturnType<typeof useCallback<handleRestore>>} 復帰
   */
  const handleRestore = useCallback(
    (e) => {
      toggleDelete(e, false)
      // toggleDelete内で既にonMarkClassificationAsPendingが呼ばれるため、ここでは不要
    },
    [toggleDelete]
  )

  const handleDrag = (e) => {
    const { active, over } = e

    if (!over || active.id === over.id) return
    const oldIndex = recordIds.indexOf(active.id)
    const newIndex = recordIds.indexOf(over.id)

    // フィルターで非表示のレコードを保持したまま、表示中レコードのみ並び替える
    const sortedArray = reorderVisibleRecordsPreservingHidden({
      sortedRecords,
      filteredRecords,
      oldIndex,
      newIndex
    })
    if (!sortedArray) return

    dispatch('UPDATE_DISPLAY_ORDER', {
      sortedArray
    })
    // 未反映状態にマーク（Reduxの状態更新を待つ）
    afterStateUpdate(() => {
      if (onMarkClassificationAsPending) {
        onMarkClassificationAsPending(selectedClassificationType)
      }
    })
  }

  // 検索とフィルターのローカルステート
  const [searchText, setSearchText] = useState('')
  const [validityFilter, setValidityFilter] = useState('all')

  const sortedRecords = useMemo(
    () =>
      _records.slice().sort((a, b) => {
        const orderA = parseInt(a.displayOrder, 10) || 0
        const orderB = parseInt(b.displayOrder, 10) || 0
        return orderA - orderB
      }),
    [_records, selectedClassificationType]
  )

  // フィルタリングロジック
  const filteredRecords = useMemo(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0) // 時刻を0時に設定して日付のみで比較

    return sortedRecords.filter((record) => {
      // 新規追加で削除されたレコードは非表示（完全削除）
      // 既存レコードの削除状態は表示（グレーアウト表示）
      if (record.isCreated && record.isDeleted) return false

      // 検索フィルター: コードまたは値が部分一致（大文字小文字区別なし）
      if (searchText.trim() !== '') {
        const searchLower = searchText.toLowerCase()
        const codeMatch = record.code?.toLowerCase().includes(searchLower) || false
        const valueMatch = record.value?.toLowerCase().includes(searchLower) || false
        if (!codeMatch && !valueMatch) return false
      }

      // 有効期限フィルター
      if (validityFilter === 'all') {
        // 全て: フィルタリングなし
        return true
      }

      const startDate = record.validStartDate ? new Date(record.validStartDate) : null
      const endDate = record.validEndDate ? new Date(record.validEndDate) : null

      if (startDate) startDate.setHours(0, 0, 0, 0)
      if (endDate) endDate.setHours(0, 0, 0, 0)

      if (validityFilter === 'valid') {
        // 有効のみ: 現在日付が有効期間内
        if (startDate && endDate) {
          return now >= startDate && now <= endDate
        } else if (startDate) {
          return now >= startDate
        } else if (endDate) {
          return now <= endDate
        } else {
          return true // 両方nullの場合は表示
        }
      }

      if (validityFilter === 'expired') {
        // 期限切れのみ: 現在日付が有効終了日より後
        return endDate && now > endDate
      }

      if (validityFilter === 'notStarted') {
        // 開始前のみ: 現在日付が有効開始日より前
        return startDate && now < startDate
      }

      return true
    })
  }, [sortedRecords, searchText, validityFilter])

  const recordIds = useMemo(() => filteredRecords.map((e) => e.key), [filteredRecords])

  /** @function handleReset 区分項目を初期状態にリセット */
  const handleReset = useCallback(() => {
    if (!selectedClassificationType) {
      return
    }

    const state = store.getState()
    const initialRecords = state.initialClassificationRecords?.[selectedClassificationType]

    // 初期状態が保存されていない場合は何もしない
    if (!initialRecords) {
      return
    }

    // 初期状態を深いコピーで取得し、日付文字列をDateオブジェクトに変換
    const resetRecords = JSON.parse(JSON.stringify(initialRecords)).map((record) => {
      // 日付文字列をDateオブジェクトに変換
      const parseDate = (dateStr) => {
        if (!dateStr || dateStr === '') return null
        return parseDateCustom(dateStr, 'yyyy/MM/dd')
      }
      return {
        ...record,
        validStartDate: parseDate(record.validStartDate),
        validEndDate: parseDate(record.validEndDate)
      }
    })

    // 区分項目を初期状態で上書き（SET_STATEで直接更新）
    dispatch('SET_STATE', {
      path: `userClassificationRecords.${selectedClassificationType}`,
      value: resetRecords
    })

    // pendingUpdatesから該当区分の情報を削除
    const { pendingUpdates } = store.getState()
    if (pendingUpdates[selectedClassificationType]) {
      const newPendingUpdates = { ...pendingUpdates }
      delete newPendingUpdates[selectedClassificationType]
      dispatch('SET_STATE', {
        path: 'pendingUpdates',
        value: newPendingUpdates
      })
    }

    // updateStatusから該当区分の情報を削除
    const { updateStatus } = store.getState()
    if (updateStatus[selectedClassificationType]) {
      const newUpdateStatus = { ...updateStatus }
      delete newUpdateStatus[selectedClassificationType]
      dispatch('SET_STATE', {
        path: 'updateStatus',
        value: newUpdateStatus
      })
    }

    // エラーをクリア
    const { errors } = store.getState()
    if (errors?.byClassification?.[selectedClassificationType]) {
      const newErrorsByClassification = { ...errors.byClassification }
      delete newErrorsByClassification[selectedClassificationType]
      dispatch('SET_STATE', {
        path: 'errors.byClassification',
        value: newErrorsByClassification
      })
    }

    // インポート関連のエラー情報をクリア（区分ごと）
    if (errors?.importWarnings?.[selectedClassificationType]) {
      const newImportWarnings = { ...errors.importWarnings }
      delete newImportWarnings[selectedClassificationType]
      dispatch('SET_STATE', {
        path: 'errors.importWarnings',
        value: newImportWarnings
      })
    }
    if (errors?.importValidationErrors?.[selectedClassificationType]) {
      const newImportValidationErrors = { ...errors.importValidationErrors }
      delete newImportValidationErrors[selectedClassificationType]
      dispatch('SET_STATE', {
        path: 'errors.importValidationErrors',
        value: newImportValidationErrors
      })
    }
    // recordValidationErrorsは区分ごとに保持されているため、該当区分のもののみ削除
    if (errors?.recordValidationErrors) {
      const newRecordValidationErrors = { ...errors.recordValidationErrors }
      // 該当区分のレコードのエラーを削除
      const records = store.getState().userClassificationRecords?.[selectedClassificationType] || []
      records.forEach((record) => {
        if (newRecordValidationErrors[record.key]) {
          delete newRecordValidationErrors[record.key]
        }
      })
      dispatch('SET_STATE', {
        path: 'errors.recordValidationErrors',
        value: newRecordValidationErrors
      })
    }

    // 区分選択時に、初期状態の件数でitemCountを更新
    dispatch('UPDATE_CLASSIFICATION_TYPE_COUNT', {
      classificationType: selectedClassificationType,
      itemCount: resetRecords.length
    })

    // DataBridgeのファイル名をクリア
    if (onReset) {
      onReset()
    }
  }, [dispatch, selectedClassificationType, store, onReset])

  return (
    <>
      <div className="base-table-header sticky top-[70px] z-[11] flex items-center justify-between bg-white pt-3 pb-6 shadow-[0_1px_2px_0_rgb(0_0_0_/_0.05),inset_0_-1px_0_0_rgb(229_231_235)]">
        <div className="text-base font-medium flex items-center gap-2">
          <span>登録数: {sortedRecords.length}件</span>
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center justify-center gap-1 px-2 py-1 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:text-gray-800 hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            title="リセット"
            disabled={!store.getState().initialClassificationRecords?.[selectedClassificationType]}
          >
            <RefreshIcon className="w-4 h-4" />
            <span>リセット</span>
          </button>
        </div>

        <div className="flex items-center gap-4">
          {/* 検索欄 */}
          <div className="form-control">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="コード・値を検索"
              className="rounded-lg text-input w-full h-10 text-left text-base"
            />
          </div>

          {/* 有効期限フィルター（他画面と共通のプルダウンに統一）
              選択値の長さで幅が変わり隣の検索欄がずれないよう、幅を固定する。 */}
          <ChrofyCustomSelect
            className="w-[160px] min-w-[160px] max-w-[160px] flex-shrink-0"
            value={validityFilter}
            onChange={(e) => setValidityFilter(e.target.value)}
            hasNoSelect={false}
            options={[
              { label: '全て', value: 'all' },
              { label: '有効のみ', value: 'valid' },
              { label: '期限切れのみ', value: 'expired' },
              { label: '開始前のみ', value: 'notStarted' }
            ]}
          />

          {/* 新規登録ボタン */}
          <RegisterButton title={'新規追加'} onClick={openModal} />
        </div>
      </div>

      {(importWarnings.length > 0 || importValidationErrors.length > 0 || classificationErrors.length > 0) && (
        <div className="mb-4 space-y-4">
          {importWarnings.length > 0 && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-yellow-800 mb-2">警告</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {importWarnings.map((warning, index) => (
                      <li key={index} className="text-sm text-yellow-700">
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {importValidationErrors.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800 mb-2">設定値に問題が発生しました</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {importValidationErrors.map((error, index) => {
                      const fieldLabelMap = {
                        value: '値',
                        code: 'コード',
                        validStartDate: '有効開始月',
                        validEndDate: '有効終了月',
                        rank: 'ランク',
                        memo: 'メモ'
                      }
                      const fieldLabel = fieldLabelMap[error.field] || error.field
                      return (
                        <li key={index} className="text-sm text-red-700">
                          {error.recordIndex + 1}行目: {fieldLabel} - {error.message}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {classificationErrors.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800 mb-2">エラーが発生しました</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {classificationErrors.map((error, index) => (
                      <li key={index} className="text-sm text-red-700">
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <DndContext onDragEnd={handleDrag}>
        <div className="setting-table is-segment relative z-0">
          {filteredRecords?.length === 0 ? (
            <>
              <table
                className="setting-table__table table-fixed w-full form-control mb-5"
                data-testid="classificationInfoTable"
              >
                <colgroup>
                  <col className="w-[50px]" />
                  <col className="w-[200px]" />
                  <col className="min-w-[200px]" />
                  <col className="w-[100px]" />
                  <col className="w-[130px]" />
                  <col className="w-[130px]" />
                  <col className="w-[120px]" />
                </colgroup>
                <thead className="sticky top-[calc(70px+4.75rem)] !z-10 shadow-sm">
                  <tr>
                    <th>≡</th>
                    <th>コード</th>
                    <th>値</th>
                    <th>ランク</th>
                    <th>有効開始月</th>
                    <th>有効終了月</th>
                    <th>状態</th>
                  </tr>
                </thead>
                <tbody></tbody>
              </table>
              {sortedRecords.length === 0 && <p className="red--text text-center py-4">データが未登録です</p>}
            </>
          ) : (
            <SortableContext strategy={verticalListSortingStrategy} items={recordIds}>
              <table
                className="setting-table__table table-fixed w-full form-control mb-5"
                data-testid="classificationInfoTable"
              >
                <colgroup>
                  <col className="w-[50px]" />
                  <col className="w-[200px]" />
                  <col className="min-w-[200px]" />
                  <col className="w-[100px]" />
                  <col className="w-[130px]" />
                  <col className="w-[130px]" />
                  <col className="w-[120px]" />
                </colgroup>
                <thead className="sticky top-[calc(70px+4.75rem)] !z-10 shadow-sm">
                  <tr>
                    <th>≡</th>
                    <th>コード</th>
                    <th>値</th>
                    <th>ランク</th>
                    <th>有効開始月</th>
                    <th>有効終了月</th>
                    <th>状態</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRecords?.map((e, index) => (
                    <UserClassificationCategoryRow
                      index={index}
                      key={e.key}
                      record={e}
                      toggleDelete={toggleDelete}
                      handleRemove={handleRemove}
                      handleRestore={handleRestore}
                      openEditModal={openEditModal}
                    />
                  ))}
                </tbody>
              </table>
            </SortableContext>
          )}
        </div>
      </DndContext>
    </>
  )
}

const UserClassificationCategoryRow = (props) => {
  const { record: e, toggleDelete, handleRemove, handleRestore, openEditModal, index } = props
  const isDeleted = e?.isDeleted

  // レコードのバリデーションエラー情報を取得
  const recordValidationErrors = useUserClassificationSelector((s) => s.errors?.recordValidationErrors || {})
  const recordErrors = recordValidationErrors[e.key] || {}

  // 各フィールドのエラー状態をチェック
  const hasCodeError = !!recordErrors.code && recordErrors.code.length > 0
  const hasValueError = !!recordErrors.value && recordErrors.value.length > 0
  const hasRankError = !!recordErrors.rank && recordErrors.rank.length > 0
  const hasValidStartDateError = !!recordErrors.validStartDate && recordErrors.validStartDate.length > 0
  const hasValidEndDateError = !!recordErrors.validEndDate && recordErrors.validEndDate.length > 0
  const hasMemoError = !!recordErrors.memo && recordErrors.memo.length > 0

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: e.key })

  // 新規作成項目は完全削除、既存項目は削除フラグ切り替え
  const onDelete = e?.isCreated ? handleRemove : toggleDelete

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const valueTrimmed = e.value != null ? String(e.value).trim() : ''
  const isValueEmpty = valueTrimmed === ''
  const emptyValueMessage = recordErrors.value?.[0] ?? '値は必須です'
  const valueCellText = isValueEmpty ? emptyValueMessage : e.value
  const valueTooltipTitle = isValueEmpty
    ? recordErrors.value?.length
      ? recordErrors.value.join('\n')
      : emptyValueMessage
    : e.value

  return (
    <tr
      data-testid={'userClassificationRow' + index}
      className={`${isDeleted && 'bg-gray-300/50 deletedRow'}`}
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <td data-testid="userClassificationTableCol_drag">
        <button className="cursor-pointer order-icon">
          <MenuIcon {...listeners} />
        </button>
      </td>
      <td
        data-testid={'userClassificationTableCol_code'}
        className={`deleteStrikeThrough ${hasCodeError ? 'bg-red-100' : ''}`}
      >
        {e.code}
      </td>
      <td data-testid={'userClassificationTableCol_value'} className={hasValueError ? 'bg-red-100' : ''}>
        <CustomTooltip title={valueTooltipTitle}>
          {(ref) => (
            <div tabIndex={0} ref={ref} className="truncate">
              <a
                className={
                  isValueEmpty
                    ? 'deleteStrikeThrough cursor-pointer text-red-600 hover:text-red-700'
                    : 'deleteStrikeThrough cursor-pointer text-blue-500'
                }
                type=""
                onClick={(event) => openEditModal(event, e)}
              >
                {valueCellText}
              </a>
            </div>
          )}
        </CustomTooltip>
      </td>
      <td
        data-testid={'userClassificationTableCol_rank'}
        className={`deleteStrikeThrough ${hasRankError ? 'bg-red-100' : ''}`}
      >
        {e.rank ?? ''}
      </td>
      <td
        data-testid={'userClassificationTableCol_validStartDate'}
        className={`deleteStrikeThrough ${hasValidStartDateError ? 'bg-red-100' : ''}`}
      >
        {formatDateAsMonth(e.validStartDate) || e._rawValidStartDate || ''}
      </td>
      <td
        data-testid={'userClassificationTableCol_validEndDate'}
        className={`deleteStrikeThrough ${hasValidEndDateError ? 'bg-red-100' : ''}`}
      >
        {formatDateAsMonth(e.validEndDate) || e._rawValidEndDate || ''}
      </td>
      <td data-testid={'userClassificationTableCol_status'}>
        <div className="flex justify-between">
          <span className={` text-sm deleteStrikeThrough ${e.status === '使用中' && 'text-green-400'}`}>
            {e.status ?? '未使用'}
          </span>
          {/* 使用中の項目は削除ボタンを非活性 */}
          <DeleteButton
            isRestore={isDeleted}
            disabled={e.status === '使用中'}
            onDelete={() => onDelete(e)}
            onRestore={() => handleRestore(e)}
          />
        </div>
      </td>
    </tr>
  )
}
